import React, { useRef, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { toPng } from "html-to-image";
import { ScanLine, Phone, Download, Loader2 } from "lucide-react";

import "./StoreSticker.css";

const storeLenseLogo = "/StorelenseLogo.png";

/**
 * StoreSticker
 *
 * Generates a printable/shareable catalogue sticker for a store.
 * The QR code encodes `storeUrl`. The whole card can be exported
 * and downloaded/shared as a PNG.
 *
 * npm install qrcode.react html-to-image
 *
 * Props:
 *  - storeName   {string} e.g. "Roll With It"
 *  - address     {string} e.g. "12 Allen Avenue, Ikeja, Lagos"
 *  - category    {string} e.g. "Others"
 *  - phoneNumber {string} e.g. "+234 907 909 1037"
 *  - storeUrl    {string} URL the QR code should open
 *  - footerText  {string} optional, defaults to StoreLense tagline
 */
export default function StoreSticker({ storeName, address, category, phoneNumber, storeUrl, footerText = "Smart Inventory. Smarter Business." }) {
  const cardRef = useRef(null);
  const [exporting, setExporting] = useState(false);

  const fileName = `${storeName || "store"}-sticker.png`.toLowerCase().replace(/\s+/g, "-");

  const exportPng = async () => {
    if (!cardRef.current) return null;
    return toPng(cardRef.current, {
      pixelRatio: 3,
      cacheBust: true,
      backgroundColor: "#ffffff",
      skipFonts: true,
    });
  };

  const handleDownload = async () => {
    setExporting(true);
    try {
      const dataUrl = await exportPng();
      if (!dataUrl) return;
      const link = document.createElement("a");
      link.download = fileName;
      link.href = dataUrl;
      link.click();
    } catch (err) {
      console.error("Failed to export sticker:", err);
    } finally {
      setExporting(false);
    }
  };

  const handleShare = async () => {
    setExporting(true);
    try {
      const dataUrl = await exportPng();
      if (!dataUrl) return;
      const res = await fetch(dataUrl);
      const blob = await res.blob();
      const file = new File([blob], fileName, { type: "image/png" });

      if (navigator.share && navigator.canShare?.({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: `${storeName} sticker`,
          text: `Scan to view ${storeName}'s product catalogue`,
        });
      } else {
        handleDownload();
      }
    } catch (err) {
      console.error("Failed to share sticker:", err);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className='ss-wrapper'>
      <div className='ss-card' ref={cardRef}>
        <div className='ss-card-inner'>
          <h1 className='ss-title'>{storeName}</h1>
          <p className='ss-address-text'>{address}</p>

          <div className='ss-divider'>
            <span className='ss-divider-line' />
            <span className='ss-divider-dot' />
            <span className='ss-divider-line' />
          </div>

          <div className='ss-category-pill'>
            <span>{category}</span>
          </div>

          <div className='ss-qr-row'>
            <div className='ss-scan-label ss-scan-label--left'>
              <ScanLine className='ss-scan-icon' strokeWidth={1.75} />
              <p>
                SCAN TO
                <br />
                VIEW OUR
                <br />
                <span className='ss-scan-highlight'>PRODUCT</span>
                <br />
                <span className='ss-scan-highlight'>CATALOGUE</span>
              </p>
            </div>

            <div className='ss-qr-box'>
              <QRCodeSVG value={storeUrl} size={185} level='M' bgColor='#ffffff' fgColor='#0F2A47' includeMargin={false} />
            </div>

            <div className='ss-scan-label ss-scan-label--right'>
              <ScanLine className='ss-scan-icon' strokeWidth={1.75} />
              <p>
                SCAN TO
                <br />
                VIEW OUR
                <br />
                <span className='ss-scan-highlight'>PRODUCT</span>
                <br />
                <span className='ss-scan-highlight'>CATALOGUE</span>
              </p>
            </div>
          </div>

          <div className='ss-contact'>
            <div className='ss-phone-icon'>
              <Phone size={18} strokeWidth={2} fill='#ffffff' />
            </div>
            <div className='ss-contact-text'>
              <span className='ss-contact-label'>CALL / WHATSAPP</span>
              <span className='ss-contact-number'>{phoneNumber}</span>
            </div>
          </div>

          <div className='ss-footer'>
            <svg className='ss-footer-curve' viewBox='0 0 500 40' preserveAspectRatio='none'>
              <path d='M0,40 Q250,-10 500,40 L500,40 L0,40 Z' fill='#0D3FAE' />
            </svg>
            <div className='ss-footer-body'>
              <span className='ss-footer-powered'>POWERED BY</span>
              <div className='ss-footer-brand'>
                <img src={storeLenseLogo} alt='StoreLense' className='ss-footer-logo' />
                <span className='ss-footer-brandname'>
                  Store
                  <span className='ss-footer-brandname--accent'>Lense</span>
                </span>
              </div>
              <span className='ss-footer-tagline'>{footerText}</span>
            </div>
          </div>
        </div>
      </div>

      <div className='ss-actions'>
        <button type='button' className='ss-btn ss-btn--primary' onClick={handleDownload} disabled={exporting}>
          {exporting ? <Loader2 size={16} className='ss-btn-spinner' /> : <Download size={16} />}
          Download PNG
        </button>
        <button type='button' className='ss-btn ss-btn--secondary' onClick={handleShare} disabled={exporting}>
          Share
        </button>
      </div>
    </div>
  );
}
